import { Fragment, useMemo, useState } from 'react';
import { LayoutRectangle, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, ChevronRightIcon, CloseIcon, HStack, IBoxProps, Icon, IconButton, ScrollView, VStack, useTheme } from 'native-base';
import { padStart } from 'lodash';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import moment from 'moment';

import { Text, Modal } from '@pimm/base';
import { CalendarEvent } from '../context/calendar-events-context';
import { BadgeCalendarEvent } from './badge-calendar-event';
import { useModalFocus } from '@app/hooks/modal-focus.hook';

type DailyEventsProps = IBoxProps & {
  date: Date;
  events: CalendarEvent[];
  hideDay?: boolean;
  isSameMonth?: boolean;
};

export const DailyEvents = ({ date, events, hideDay = false, isSameMonth, ...props }: DailyEventsProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const modalEvents = useModalFocus();
  const [layout, setLayout] = useState<LayoutRectangle>();

  const slicedEvents = useMemo(() => {
    const maxRows = (layout?.height ? Math.floor(layout.height / 26) : 0) - 1; // -1 for the pressable footer
    return events.slice(0, maxRows);
  }, [events, layout?.height]);

  return (
    <Box flexDirection={{ md: 'column', xl: 'row' }} px={{ md: 1.5, xl: 2 }} {...props}>
      {!hideDay && (
        <Box pt={3} pr={3}>
          <Text size="xl" color={isSameMonth ? 'black' : 'gray.500'}>
            {padStart(`${date.getDate()}`, 2, '0')}
          </Text>
        </Box>
      )}

      <VStack flex={1} space={0.5} pt={{ md: hideDay ? 3 : 1, xl: 3 }} onLayout={e => setLayout(e.nativeEvent.layout)}>
        {!!layout?.height && (
          <Fragment>
            {slicedEvents.map(event => (
              <BadgeCalendarEvent key={`${event.type}.${event.referenceId}`} w="full" rounded="sm" date={date} event={event} />
            ))}

            {!hideDay && slicedEvents.length < events.length && (
              <Pressable onPress={modalEvents.setOpen}>
                <HStack space={1.5} alignItems="center" justifyContent="space-between" py={1} pl={1.5} pr={0.5} bgColor="gray.50" rounded="sm">
                  <Box flex={1}>
                    <Text size="sm" color="gray.900" lineHeight="sm">
                      +{events.length - slicedEvents.length} {t('calendar:see_more')}
                    </Text>
                  </Box>
                  <ChevronRightIcon size={3} color="gray.900" />
                </HStack>
              </Pressable>
            )}
          </Fragment>
        )}

        <Modal
          size="xl"
          _content={{ rounded: 'xl', maxW: '30%', minH: 300 }}
          isOpen={modalEvents.isOpen}
          noPadding
          hideClose
          onClose={modalEvents.setHide}
        >
          <HStack
            alignItems="center"
            justifyContent="space-between"
            pl={4}
            pr={2}
            h="50px"
            borderBottomWidth={1}
            borderBottomColor="gray.100"
          >
            <Text size="lg" fontWeight={600} color="gray.900">
              {t('calendar:events')} ({moment(date).format('MMM DD, YYYY')})
            </Text>

            <IconButton
              rounded="lg"
              variant="unstyled"
              p={2}
              boxSize={7}
              _pressed={{ bg: 'gray.50' }}
              icon={<CloseIcon size="18px" color={colors.gray[700]} />}
              onPress={modalEvents.setHide}
            />
          </HStack>

          <ScrollView maxHeight={460}>
            <VStack space={1} p={4}>
              {events.map((event, index) => (
                <BadgeCalendarEvent
                  key={`${date.toISOString()}-modal-${index}`}
                  w="full"
                  rounded="sm"
                  date={date}
                  event={event}
                  minHeight="32px"
                />
              ))}
            </VStack>
          </ScrollView>
        </Modal>
      </VStack>
    </Box>
  );
};
